import React, { useEffect, useMemo, useState } from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
  ReferenceLine,
} from 'recharts';
import { LineChart as LineChartIcon } from 'lucide-react';
import { supabase } from '../lib/supabase';
import {
  aggregateHistoryChartPoints,
  buildCombinedChartRows,
  buildHistoryChartPoints,
  fetchForumStatSnapshots,
  formatHistoryDelta,
  getHistoryMetricValue,
  HISTORY_METRIC_COLORS,
  HISTORY_METRIC_LABELS,
  HISTORY_METRICS,
  toPeriodDeltaChartPoints,
  type ForumForHistory,
  type HistoryGranularity,
  type HistoryMetric,
} from '../utils/forumStatSnapshot';

interface Props {
  forum: ForumForHistory;
  darkMode: boolean;
  refreshKey?: number;
}

type ViewMode = 'separate' | 'combined';

const GRANULARITY_LABELS: Record<HistoryGranularity, string> = {
  daily: 'Günlük',
  weekly: 'Haftalık',
  monthly: 'Aylık',
  yearly: 'Yıllık',
};

const VIEW_MODE_LABELS: Record<ViewMode, string> = {
  separate: 'Ayrı grafik',
  combined: 'Tek grafik',
};

function toggleMetricInSet(prev: Set<HistoryMetric>, metric: HistoryMetric): Set<HistoryMetric> {
  const next = new Set(prev);
  if (next.has(metric)) {
    if (next.size > 1) next.delete(metric);
  } else {
    next.add(metric);
  }
  return next;
}

type DotProps = {
  cx?: number;
  cy?: number;
  index?: number;
  payload?: { isActual?: boolean | number };
};

/** Yalnızca gerçek güncelleme noktalarında işaret göster; taşınan (düz çizgi) noktalarda gizle. */
function makeActualDotRenderer(color: string) {
  return function ActualDot(props: DotProps) {
    const { cx, cy, index, payload } = props;
    const key = `dot-${index ?? Math.random()}`;
    if (typeof cx !== 'number' || typeof cy !== 'number') return <g key={key} />;
    if (!payload?.isActual) return <g key={key} />;
    return <circle key={key} cx={cx} cy={cy} r={3.5} fill={color} stroke={color} strokeWidth={1} />;
  };
}

const ForumHistoryChart: React.FC<Props> = ({ forum, darkMode, refreshKey = 0 }) => {
  const [metric, setMetric] = useState<HistoryMetric>('members');
  const [viewMode, setViewMode] = useState<ViewMode>('separate');
  const [granularity, setGranularity] = useState<HistoryGranularity>('daily');
  const [visibleMetrics, setVisibleMetrics] = useState<Set<HistoryMetric>>(
    () => new Set(HISTORY_METRICS)
  );
  const [loading, setLoading] = useState(true);
  const [snapshots, setSnapshots] = useState<Awaited<ReturnType<typeof fetchForumStatSnapshots>>>([]);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      try {
        const rows = await fetchForumStatSnapshots(supabase, forum.id);
        if (!cancelled) setSnapshots(rows);
      } catch {
        if (!cancelled) setSnapshots([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [forum.id, refreshKey, forum.updated_at]);

  const chartPoints = useMemo(
    () =>
      toPeriodDeltaChartPoints(
        aggregateHistoryChartPoints(buildHistoryChartPoints(snapshots, forum), granularity)
      ),
    [snapshots, forum, granularity]
  );

  const actualCount = useMemo(
    () => chartPoints.filter((p) => p.isActual).length,
    [chartPoints]
  );

  const separateChartData = useMemo(
    () =>
      chartPoints.map((p) => ({
        label: p.label,
        value: getHistoryMetricValue(p, metric),
        isActual: p.isActual,
      })),
    [chartPoints, metric]
  );

  const visibleMetricsList = useMemo(
    () => HISTORY_METRICS.filter((m) => visibleMetrics.has(m)),
    [visibleMetrics]
  );

  const combinedChartData = useMemo(
    () => buildCombinedChartRows(chartPoints, visibleMetricsList),
    [chartPoints, visibleMetricsList]
  );

  const gridColor = darkMode ? '#374151' : '#e5e7eb';
  const axisColor = darkMode ? '#9ca3af' : '#6b7280';
  const zeroLineColor = darkMode ? '#6b7280' : '#9ca3af';
  const lineColor = HISTORY_METRIC_COLORS[metric];

  const hasEnoughPoints = snapshots.length >= 2 && chartPoints.length >= 1;
  const manyPoints = chartPoints.length > 6;

  const controlBtn = (active: boolean) =>
    `px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
      active
        ? 'bg-indigo-600 text-white'
        : darkMode
          ? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
    }`;

  const metricChip = (m: HistoryMetric, active: boolean) =>
    `inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors border ${
      active
        ? 'border-transparent text-white'
        : darkMode
          ? 'border-gray-600 bg-gray-800/60 text-gray-400 hover:text-gray-200'
          : 'border-gray-200 bg-white text-gray-500 hover:text-gray-800'
    }`;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-indigo-500" />
      </div>
    );
  }

  if (!hasEnoughPoints) {
    return (
      <div
        className={`text-center py-12 rounded-lg border border-dashed ${
          darkMode ? 'border-gray-600 text-gray-400' : 'border-gray-300 text-gray-500'
        }`}
      >
        <LineChartIcon className="mx-auto mb-3 opacity-50" size={40} />
        <p className="font-medium">Artış grafiği henüz hazır değil</p>
        <p className="text-sm mt-2 max-w-md mx-auto">
          Dönemsel artış için en az iki gerçek güncelleme kaydı gerekir. İkinci kayıttan itibaren
          grafik, toplam yerine bir önceki döneme göre artışı (veya azalmayı) gösterir.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between">
        <div className="flex flex-wrap gap-2">
          {(Object.keys(VIEW_MODE_LABELS) as ViewMode[]).map((mode) => (
            <button
              key={mode}
              type="button"
              onClick={() => setViewMode(mode)}
              className={controlBtn(viewMode === mode)}
            >
              {VIEW_MODE_LABELS[mode]}
            </button>
          ))}
        </div>
        <div className="flex flex-wrap gap-2">
          {(Object.keys(GRANULARITY_LABELS) as HistoryGranularity[]).map((g) => (
            <button
              key={g}
              type="button"
              onClick={() => setGranularity(g)}
              className={controlBtn(granularity === g)}
            >
              {GRANULARITY_LABELS[g]}
            </button>
          ))}
        </div>
      </div>

      {viewMode === 'separate' ? (
        <div className="flex flex-wrap gap-2">
          {HISTORY_METRICS.map((m) => (
            <button
              key={m}
              type="button"
              onClick={() => setMetric(m)}
              className={controlBtn(metric === m)}
            >
              {HISTORY_METRIC_LABELS[m]}
            </button>
          ))}
        </div>
      ) : (
        <div
          className={`flex flex-wrap gap-2 p-3 rounded-lg ${
            darkMode ? 'bg-gray-800/50' : 'bg-gray-50'
          }`}
          role="group"
          aria-label="Grafikte gösterilecek metrikler"
        >
          {HISTORY_METRICS.map((m) => {
            const active = visibleMetrics.has(m);
            return (
              <button
                key={m}
                type="button"
                onClick={() => setVisibleMetrics((prev) => toggleMetricInSet(prev, m))}
                className={metricChip(m, active)}
                style={active ? { backgroundColor: HISTORY_METRIC_COLORS[m] } : undefined}
                aria-pressed={active}
              >
                <span
                  className={`w-3 h-3 rounded-sm border shrink-0 ${
                    active ? 'bg-white/90 border-white/90' : 'border-current bg-transparent'
                  }`}
                  aria-hidden
                />
                {HISTORY_METRIC_LABELS[m]}
              </button>
            );
          })}
        </div>
      )}

      <div className={`${darkMode ? 'bg-gray-900/50' : 'bg-white'} rounded-lg p-2 sm:p-4`}>
        <ResponsiveContainer width="100%" height={320}>
          {viewMode === 'separate' ? (
            <LineChart data={separateChartData} margin={{ top: 8, right: 16, left: 8, bottom: 8 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={gridColor} />
              <XAxis
                dataKey="label"
                tick={{ fill: axisColor, fontSize: 11 }}
                interval="preserveStartEnd"
                minTickGap={28}
                angle={manyPoints ? -25 : 0}
                textAnchor={manyPoints ? 'end' : 'middle'}
                height={manyPoints ? 56 : 32}
              />
              <YAxis
                tick={{ fill: axisColor, fontSize: 12 }}
                tickFormatter={(v: number) => formatHistoryDelta(v)}
                width={64}
              />
              <ReferenceLine y={0} stroke={zeroLineColor} strokeDasharray="4 4" />
              <Tooltip
                contentStyle={{
                  backgroundColor: darkMode ? '#1f2937' : '#fff',
                  border: `1px solid ${gridColor}`,
                  borderRadius: '8px',
                  color: darkMode ? '#f3f4f6' : '#111827',
                }}
                formatter={(value: number) => [
                  formatHistoryDelta(value),
                  HISTORY_METRIC_LABELS[metric],
                ]}
                labelFormatter={(label) => `Dönem: ${label}`}
              />
              <Line
                type="monotone"
                dataKey="value"
                stroke={lineColor}
                strokeWidth={2}
                dot={makeActualDotRenderer(lineColor)}
                activeDot={{ r: 6 }}
                isAnimationActive={!manyPoints}
              />
            </LineChart>
          ) : (
            <LineChart data={combinedChartData} margin={{ top: 8, right: 16, left: 8, bottom: 8 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={gridColor} />
              <XAxis
                dataKey="label"
                tick={{ fill: axisColor, fontSize: 11 }}
                interval="preserveStartEnd"
                minTickGap={28}
                angle={manyPoints ? -25 : 0}
                textAnchor={manyPoints ? 'end' : 'middle'}
                height={manyPoints ? 56 : 32}
              />
              <YAxis
                tick={{ fill: axisColor, fontSize: 12 }}
                tickFormatter={(v: number) => `${v}`}
                width={48}
                label={{
                  value: 'Göreli artış',
                  angle: -90,
                  position: 'insideLeft',
                  fill: axisColor,
                  fontSize: 11,
                }}
              />
              <ReferenceLine y={0} stroke={zeroLineColor} strokeDasharray="4 4" />
              <Tooltip
                contentStyle={{
                  backgroundColor: darkMode ? '#1f2937' : '#fff',
                  border: `1px solid ${gridColor}`,
                  borderRadius: '8px',
                  color: darkMode ? '#f3f4f6' : '#111827',
                }}
                formatter={(value: number, name: string, item) => {
                  const metricKey = name as HistoryMetric;
                  const payload = (item as { payload?: Record<string, string | number> })?.payload;
                  const raw = payload?.[`raw_${metricKey}`];
                  const rawLabel = typeof raw === 'number' ? formatHistoryDelta(raw) : String(value);
                  return [`${rawLabel} (${value})`, HISTORY_METRIC_LABELS[metricKey]];
                }}
                labelFormatter={(label) => `Dönem: ${label}`}
              />
              <Legend
                wrapperStyle={{ fontSize: 12, color: axisColor }}
                formatter={(value: string) => HISTORY_METRIC_LABELS[value as HistoryMetric] ?? value}
              />
              {visibleMetricsList.map((m) => (
                <Line
                  key={m}
                  type="monotone"
                  dataKey={m}
                  name={m}
                  stroke={HISTORY_METRIC_COLORS[m]}
                  strokeWidth={2}
                  dot={makeActualDotRenderer(HISTORY_METRIC_COLORS[m])}
                  activeDot={{ r: 5 }}
                  hide={!visibleMetrics.has(m)}
                  isAnimationActive={!manyPoints}
                />
              ))}
            </LineChart>
          )}
        </ResponsiveContainer>
      </div>

      <p className={`text-xs ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
        Grafik yalnızca gerçek güncelleme dönemlerini gösterir; ara günler atlanır. Her nokta,
        bir önceki güncellemeye göre artıştır (veya azalıştır).
        {viewMode === 'combined'
          ? ' Tek grafik modunda her metrik kendi zirve artışına göre göreli ölçeklenir; gerçek artış ipucunda görünür.'
          : ''}
        {granularity !== 'daily' &&
          ' Haftalık, aylık ve yıllık görünümde aynı dönemdeki son kayıt kullanılır.'}
        {' '}
        Toplam {actualCount} güncelleme dönemi.
      </p>
    </div>
  );
};

export default ForumHistoryChart;
